import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, Copy, Check, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Shown once per visit: remembered in the browser so refreshing, opening another tab or coming back
// from Stripe doesn't bring it back. It can show again after a day; never again once they've signed up.
const SEEN_KEY = "zebaa_popup_seen_at";
const SUBSCRIBED_KEY = "zebaa_popup_subscribed";
const SHOW_AGAIN_AFTER_MS = 24 * 60 * 60 * 1000;
let shownThisLoad = false;

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode: the in-memory flag still applies */ } },
};
const alreadySeen = () =>
  shownThisLoad || !!store.get(SUBSCRIBED_KEY) || Date.now() - Number(store.get(SEEN_KEY) || 0) < SHOW_AGAIN_AFTER_MS;

export const NewsletterPopup = () => {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { code, percent }
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only on the home page, and only once per visit (see alreadySeen).
    if (location.pathname !== "/") {
      setOpen(false);
      return;
    }
    if (alreadySeen()) return;
    shownThisLoad = true; // set before the request so a double-run effect can't queue it twice
    let timer;
    let fired = false;
    api.get("/settings").then(({ data }) => {
      if (!data || !data.popup_enabled) return;
      setSettings(data);
      timer = setTimeout(() => {
        if (window.location.pathname !== "/") { shownThisLoad = false; return; } // they left home first
        fired = true;
        setOpen(true);
        store.set(SEEN_KEY, String(Date.now()));
      }, (data.popup_delay_seconds || 3) * 1000);
    });
    return () => {
      clearTimeout(timer);
      if (timer && !fired) shownThisLoad = false; // left home before it appeared: allow it next time
    };
  }, [location.pathname]);

  const close = () => {
    setOpen(false);
    setDismissed(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email: email.trim(), source: "popup" });
      const code = data.discount_code || settings?.popup_code;
      const percent = data.discount_percent ?? settings?.popup_discount_percent;
      if (code) store.set("zebaa_welcome_code", code);
      store.set(SUBSCRIBED_KEY, "1");
      setResult({ code, percent });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    const code = result.code;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        throw new Error("clipboard unavailable");
      }
      setCopied(true);
      toast.success("Code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for iframes / non-secure contexts / older browsers
      try {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand failed");
        setCopied(true);
        toast.success("Code copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error(`Copy the code manually: ${code}`);
      }
    }
  };

  if (!settings) return null;

  const goldStyle = {
    background: "linear-gradient(180deg, #f8ecbc 0%, #edd48c 38%, #d9b45f 58%, #f4e2a4 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))",
  };

  // Once dismissed, keep the offer reachable via a small floating pill.
  if (!open) {
    if (!dismissed) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="popup-reopen"
        aria-label="Reopen welcome offer"
        className="fixed bottom-5 right-5 z-[95] flex items-center justify-center gap-2 bg-pink text-white rounded-full shadow-xl hover:bg-pink-hover transition-colors animate-fade-up w-11 h-11 sm:w-auto sm:h-auto sm:pl-3.5 sm:pr-4 sm:py-2.5"
      >
        <Gift className="w-4 h-4" />
        <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-[0.16em]">
          {result ? "Your Code" : "Discount"}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="newsletter-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-white w-full max-w-3xl overflow-hidden shadow-2xl animate-fade-up grid grid-cols-1 md:grid-cols-2">
        <button onClick={close} aria-label="Close popup" data-testid="popup-close" className="absolute top-3 right-3 z-20 text-charcoal/60 hover:text-pink transition-colors bg-white/70 rounded-full p-1">
          <X className="w-5 h-5" />
        </button>

        {/* Left: model photo */}
        <div className="hidden md:block relative">
          <img
            src="/newsletter-model.jpg"
            alt="Zebaa model in luxury embroidered suit"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/5" />
        </div>

        {/* Right: content */}
        <div className="p-8 sm:p-10 flex flex-col justify-center text-center">
          {!result ? (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] text-pink font-medium">{settings.popup_eyebrow || "First Timer?"}</p>
              <div className="font-sans font-extrabold text-4xl sm:text-5xl tracking-tight mt-3" style={goldStyle} data-testid="popup-discount">
                Exclusive Discount
              </div>
              <h3 className="font-serif text-2xl text-charcoal mt-3 leading-snug">{settings.popup_headline}</h3>
              <form onSubmit={submit} className="mt-6">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  data-testid="popup-email-input"
                  className="w-full border-0 border-b border-gray-300 px-1 py-3 text-sm focus:outline-none focus:border-pink text-center bg-transparent"
                />
                {error && <p className="text-xs text-pink mt-2" data-testid="popup-email-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="popup-submit"
                  className="w-full bg-pink text-white font-semibold py-3.5 mt-5 rounded-full text-xs uppercase tracking-[0.22em] hover:bg-pink-hover transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Please wait..." : (settings.popup_button_text || "Subscribe")}
                </button>
              </form>
              <button onClick={close} data-testid="popup-dismiss" className="text-xs font-semibold uppercase tracking-[0.18em] text-charcoal mt-5 hover:text-pink transition-colors">{settings.popup_decline_text || "No, thanks"}</button>
              <p className="text-[11px] text-gray-400 mt-5 leading-relaxed max-w-xs mx-auto">{settings.popup_subtext}</p>
            </>
          ) : (
            <div data-testid="popup-success">
              <div className="font-sans font-extrabold text-4xl tracking-tight" style={goldStyle}>{settings.popup_success_title || "You're in!"}</div>
              <h3 className="font-serif text-xl text-charcoal mt-3">{settings.popup_success_subtext || "Here's your code"}</h3>
              <div className="mt-4 flex items-stretch">
                <div className="flex-1 border-2 border-dashed border-pink text-pink font-semibold tracking-[0.2em] text-xl flex items-center justify-center py-3" data-testid="popup-code">
                  {result.code}
                </div>
                <button
                  onClick={copyCode}
                  data-testid="popup-copy"
                  className="bg-pink text-white px-4 flex items-center gap-1.5 text-xs uppercase tracking-wide hover:bg-pink-hover transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="mt-5 bg-pink/5 border border-pink/20 rounded-md px-4 py-3 text-sm text-charcoal">
                Enter this code at checkout to save {result.percent}% - no account needed.
              </div>
              <Link
                to="/shop/all"
                onClick={close}
                data-testid="popup-shop-now"
                className="block w-full bg-pink text-white py-3.5 mt-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-pink-hover transition-colors"
              >
                Shop Now &amp; Save {result.percent}%
              </Link>
              <button onClick={close} className="text-xs font-semibold uppercase tracking-[0.18em] text-charcoal mt-4 hover:text-pink transition-colors" data-testid="popup-continue">
                Maybe later
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
